import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import PartnerLegalAcceptance from "@/components/PartnerLegalAcceptance";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/config/api";

type LegalStatusResponse = {
  requiresAcceptance: boolean;
  hasPartnerDocument: boolean;
  accepted: {
    partner_contract?: boolean;
    partner_lgpd?: boolean;
    partner_marketing?: boolean;
  };
  bundle?: {
    versionLabel?: string;
  };
};

const PartnerLegalGate = () => {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<LegalStatusResponse | null>(null);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const authState = useMemo(() => {
    if (typeof window === "undefined") {
      return { token: "", user: null as any };
    }
    const token = localStorage.getItem("token") || "";
    try {
      const rawUser = localStorage.getItem("user");
      const user = rawUser ? JSON.parse(rawUser) : null;
      return { token, user };
    } catch {
      return { token, user: null as any };
    }
  }, [location.key]);

  const isPartner = authState.user?.user_type === "partner";
  const shouldBlock = Boolean(isPartner && status && (!status.hasPartnerDocument || status.requiresAcceptance));

  useEffect(() => {
    const loadStatus = async () => {
      if (!authState.token || !isPartner) {
        setReady(true);
        setStatus(null);
        return;
      }
      try {
        setLoading(true);
        const response = await apiRequest("/auth/partner-legal/status", {
          headers: { Authorization: `Bearer ${authState.token}` },
        });
        const json = await response.json();
        const nextStatus = (json?.data || null) as LegalStatusResponse | null;
        setStatus(nextStatus);
        setContractAccepted(false);
        setLgpdAccepted(Boolean(nextStatus?.accepted?.partner_lgpd));
        setMarketingAccepted(Boolean(nextStatus?.accepted?.partner_marketing));
      } catch (error: any) {
        toast({
          title: "Não foi possível validar os termos",
          description: error?.message || "Tente novamente em alguns instantes.",
        });
      } finally {
        setLoading(false);
        setReady(true);
      }
    };

    void loadStatus();
  }, [authState.token, isPartner, location.pathname, toast]);

  const handleAccept = async () => {
    try {
      setLoading(true);
      setSubmitError("");
      const response = await apiRequest("/auth/partner-legal/accept", {
        method: "POST",
        headers: { Authorization: `Bearer ${authState.token}` },
        body: JSON.stringify({
          contractAccepted,
          lgpdAccepted,
          marketingAccepted,
        }),
      });
      await response.json();
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              requiresAcceptance: false,
              hasPartnerDocument: true,
              accepted: {
                partner_contract: true,
                partner_lgpd: true,
                partner_marketing: marketingAccepted,
              },
            }
          : prev
      );
      toast({
        title: "Aceite atualizado",
        description: "A nova versão do contrato foi registrada com sucesso.",
      });
    } catch (error: any) {
      setSubmitError(error?.message || "Não foi possível concluir o aceite agora.");
    } finally {
      setLoading(false);
    }
  };

  if (!ready || !isPartner) {
    return null;
  }

  return (
    <Dialog open={shouldBlock}>
      <DialogContent
        className="max-h-[92vh] max-w-[min(96vw,72rem)] overflow-y-auto rounded-[28px] border border-[#D6DFE6] bg-white p-0"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="border-b border-[#D6DFE6] bg-[#215834] px-6 py-6 text-left">
          <DialogTitle className="font-fraunces text-2xl text-white">
            {!status?.hasPartnerDocument ? "Complete seu identificador jurídico" : "Novo aceite de termos obrigatório"}
          </DialogTitle>
          <DialogDescription className="font-author text-sm text-white/80">
            {!status?.hasPartnerDocument
              ? "Para manter validade jurídica do clickwrap, precisamos do seu CPF ou CNPJ antes de liberar a área do parceiro."
              : `A versão ${status?.bundle?.versionLabel || "atual"} do contrato exige um novo aceite eletrônico antes de continuar.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-6">
          {!status?.hasPartnerDocument ? (
            <div className="rounded-3xl border border-[#E9E6DC] bg-[#F8F5EF] p-5 font-author text-sm leading-7 text-[#52605C]">
              Vá para as configurações da conta, preencha seu CPF ou CNPJ e volte para concluir o aceite da versão vigente.
            </div>
          ) : (
            <PartnerLegalAcceptance
              contractAccepted={contractAccepted}
              lgpdAccepted={lgpdAccepted}
              marketingAccepted={marketingAccepted}
              onContractAcceptedChange={setContractAccepted}
              onLgpdAcceptedChange={setLgpdAccepted}
              onMarketingAcceptedChange={setMarketingAccepted}
              disabled={loading}
              contractError={!contractAccepted && submitError ? "Confirme o contrato para continuar." : ""}
              lgpdError={!lgpdAccepted && submitError ? "Autorize a LGPD para continuar." : ""}
            />
          )}

          {submitError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {submitError}
            </div>
          ) : null}
        </div>

        <DialogFooter className="border-t border-[#D6DFE6] bg-white px-6 py-4 sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="border-[#D6DFE6] text-[#215834]"
            onClick={() => {
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              navigate("/login");
            }}
          >
            Sair
          </Button>

          {!status?.hasPartnerDocument ? (
            <Button type="button" className="bg-[#215834] text-white hover:bg-[#184327]" onClick={() => navigate("/configuracoes")}>
              Ir para configurações
            </Button>
          ) : (
            <Button type="button" disabled={loading} className="bg-[#215834] text-white hover:bg-[#184327]" onClick={handleAccept}>
              {loading ? "Registrando aceite..." : "Aceitar nova versão"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerLegalGate;
